import type { AuthenticationExtensionsClientOutputs } from '@simplewebauthn/browser'
import { useMutation } from '@tanstack/react-query'
import { createPasskey, rememberTouch, touchPasskey } from './passkeys'
import { useTRPCClient } from './trpc'

/*
 * The two passkey ceremonies as the pages run them: the options from the server, the device,
 * and the answer back to the server. Each one starts from a press of a button (Safari asks for
 * it), and its error says what failed (passkeyErrorMessage).
 */

/** The extension results of the browser's answer, as the plain record the API takes. */
function plain(outputs: AuthenticationExtensionsClientOutputs): Record<string, unknown> {
  return { ...outputs }
}

/**
 * Touches one of the person's passkeys: the sign-in's second step, or the re-authentication of a
 * sensitive action (the server tells them apart by the session). Without a connection it fails at
 * once, and its error says so: paused, as React Query leaves a call by default, the dialog that
 * asks for it would wait with no way out, and it would go later with no press of a button.
 */
export function useTouchPasskey() {
  const client = useTRPCClient()
  return useMutation({
    mutationFn: async () => {
      const options = await client.passkey.authenticationOptions.query()
      const response = await touchPasskey(options)
      await client.passkey.authenticate.mutate({
        response: { ...response, clientExtensionResults: plain(response.clientExtensionResults) },
      })
      rememberTouch()
    },
    networkMode: 'always',
  })
}

/**
 * Registers a passkey on this device (or on a phone, through the browser): the first one with the
 * operator tool's permission, another one from a session that touched its passkey less than 5
 * minutes ago. Answers how many the person has now.
 */
export function useRegisterPasskey() {
  const client = useTRPCClient()
  return useMutation({
    mutationFn: async () => {
      const options = await client.passkey.registrationOptions.query()
      const response = await createPasskey(options)
      return client.passkey.register.mutate({
        response: { ...response, clientExtensionResults: plain(response.clientExtensionResults) },
      })
    },
  })
}
